// Tracks the live-data websockets that have successfully opened at least once.
//
// A cold SPA load (deep-link straight to a record in a fresh tab) bootstraps
// auth/session, the primary `/ui` socket and the per-record live-data socket all
// at once. If the per-record socket's FIRST connection attempt fails during that
// cold-load window, the browser emits a native `error` event that
// ReconnectingWebSocket re-dispatches to our `error` listeners -- but the socket
// then retries and connects, so live data works fine. The transient first-connect
// error must not surface a "Could not load live data" toast: nothing is durably
// broken, and a genuine total outage is already communicated by the primary `/ui`
// "Connection to backend lost" banner (its own socket fails too). The suppression
// on `isUiWebsocketDown()` does not cover this because during cold load the primary
// socket has not yet been marked down, leaving a race window where the secondary
// socket's first-connect error leaks a toast (~50% of cold loads).
//
// So an `error` fired BEFORE a socket has ever opened is treated as a transient
// initial-connection failure and is not toasted; once the socket has opened at
// least once, a later `error` (a genuine mid-session live-data drop) still toasts.
//
// A WeakSet keyed by the socket instance: pooled sockets are deleted from the pool
// when closed and never handed out again, so a marked instance is never reused and
// is free to be garbage-collected.
const everOpenedSockets = new WeakSet<object>();

// Mark a socket as having opened successfully. Call from the socket's `open`
// listener (and defensively when a pooled socket is reused while already OPEN).
export const markSocketOpened = (socket: object | null | undefined): void => {
    if (socket) {
        everOpenedSockets.add(socket);
    }
};

// True once this socket has opened at least once (see above). Read from a
// live-data socket's `error` listener before deciding to toast: a false result
// means the error is a transient first-connect failure that will be retried.
export const hasSocketEverOpened = (
    socket: object | null | undefined
): boolean => (socket ? everOpenedSockets.has(socket) : false);
